// Video source types
export enum VideoSourceType {
    YOUTUBE = 'youtube',
    YOUTUBE_SHORTS = 'youtube_shorts',
    S3_DIRECT = 's3_direct',
    VIMEO = 'vimeo',
}

// Video quality options
export enum VideoQuality {
    AUTO = 'auto',
    HIGH = 'high',
    MEDIUM = 'medium',
    LOW = 'low',
}

// Video player states
export enum VideoPlayerState {
    IDLE = 'idle',
    LOADING = 'loading',
    PLAYING = 'playing',
    PAUSED = 'paused',
    ENDED = 'ended',
    ERROR = 'error',
}

// Device types for responsive behavior
export enum DeviceType {
    MOBILE = 'mobile',
    DESKTOP = 'desktop',
}

// Video source data structure
export interface VideoSource {
    id: string;
    type: VideoSourceType;
    url: string;
    quality?: VideoQuality;
    duration?: number; // in seconds
    locked: boolean;
}

// Video metadata
export interface VideoMetadata {
    title: string;
    description?: string;
    thumbnail?: string;
    thumbnailWidth?: number;
    thumbnailHeight?: number;
    duration?: string;
    isExclusive?: boolean;
    isAcko?: boolean;
    authorName?: string;
    mediaSource?: string;
}

// Video player configuration
export interface VideoPlayerConfig {
    autoplay?: boolean;
    muted?: boolean;
    controls?: boolean;
    loop?: boolean;
    preload?: 'none' | 'metadata' | 'auto';
    showTitle?: boolean;
    showProgress?: boolean;
    showVolume?: boolean;
    showClose?: boolean;
}

// Video player state
export interface VideoPlayerStateData {
    currentTime: number;
    duration: number;
    progress: number; // 0-100
    volume: number; // 0-1
    isMuted: boolean;
    isPlaying: boolean;
    isPaused: boolean;
    isEnded: boolean;
    isLoading: boolean;
    hasError: boolean;
    errorMessage?: string;
}

// Video player events
export interface VideoPlayerEvents {
    onPlay?: () => void;
    onPause?: () => void;
    onEnded?: () => void;
    onTimeUpdate?: (currentTime: number, duration: number) => void;
    onProgress?: (progress: number) => void;
    onVolumeChange?: (volume: number, isMuted: boolean) => void;
    onError?: (error: string) => void;
    onReady?: () => void;
    onClose?: () => void;
    onReplay?: () => void;
}

// Video card props
export interface VideoCardProps {
    video: VideoSource;
    metadata: VideoMetadata;
    onClick?: (video: VideoSource, metadata: VideoMetadata) => void;
    className?: string;
    showBadges?: boolean;
    isLoggedIn?: boolean;
    brandName?: string;
    modelName?: string;
    cityName?: string;
    carAvailability?: string;
    pageType?: string;
}

// Video grid props
export interface VideoGridProps {
    title: string;
    videos: Array<{
        video: VideoSource;
        metadata: VideoMetadata;
    }>;
    className?: string;
    layout?: 'mobile' | 'desktop' | 'responsive';
    isLoggedIn?: boolean;
    brandName?: string;
    modelName?: string;
    cityName?: string;
    carAvailability?: string;
    pageType?: string;
}

// Video player component props
export interface VideoPlayerProps {
    video: VideoSource;
    metadata: VideoMetadata;
    config?: VideoPlayerConfig;
    events?: VideoPlayerEvents;
    deviceType: DeviceType;
    isVisible: boolean;
    className?: string;
}

// Video modal props
export interface VideoModalProps {
    video: VideoSource | null;
    metadata: VideoMetadata | null;
    isOpen: boolean;
    onClose: () => void;
    deviceType: DeviceType;
    config?: VideoPlayerConfig;
    events?: Omit<VideoPlayerEvents, 'onClose'>;
}

// YouTube specific configuration
export interface YouTubeConfig {
    apiKey?: string;
    playerId?: string;
    playerVars?: Record<string, any>;
}

// S3 video configuration
export interface S3VideoConfig {
    region?: string;
    bucket?: string;
    cloudFrontDomain?: string;
}

// Video service configuration
export interface VideoServiceConfig {
    youtube?: YouTubeConfig;
    s3?: S3VideoConfig;
    defaultQuality?: VideoQuality;
    enableAnalytics?: boolean;
}
